/** F.8 — `←` / `→` = previous / next record while the record panel is open.
 *
 * A leaf module (no AG, no grid) so the guard can be tested with the REAL record panel mounted and
 * focused. That matters more here than anywhere else in this file's history: the handler shipped
 * inside `CrmListGrid` and **never fired once**, because the record panel declares `role="dialog"`
 * and focuses itself on open, which satisfied the handler's own "a modal has focus" bail-out. Every
 * test of it dispatched keys from `document.body` — where the guard passes — so the whole feature was
 * dead behind a green suite and a tooltip that advertised it.
 *
 * Spec: `docs/specs/strategic-architecture/iot-grid-active-row-spec.md` §4.
 */
import { useEffect } from 'react';

/**
 * Marks the record panel root as "not a modal for keyboard-nav purposes".
 *
 * ⚠ Deliberately NOT the panel's existing `data-crm-panel-keepopen`, and the reason is its CONTRACT,
 * not its current placement. That attribute means "the panel, or a surface the panel SPAWNED, so the
 * click-away handler must not close the record" — as of this slice it happens to sit only on the panel
 * root, but its documented job is to spread to spawned surfaces (the fact-edit modal, the mail modal,
 * the note overlay). Overloading it with the OPPOSITE meaning here — "nav keys are allowed in this
 * dialog" — means the day someone stamps it on a spawned modal, exactly as its contract invites, `←`
 * and `→` silently start swapping the record out from under a writer. Two meanings, two attributes.
 */
export const RECORD_PANEL_ATTR = 'data-crm-record-panel';

/**
 * Opt-in "nav keys off while I am mounted" marker, for an overlay that genuinely cannot be
 * `role="dialog"`. Prefer `role="dialog"` — it is the a11y-correct answer AND it is picked up below
 * for free. This exists so a future non-dialog overlay has a one-attribute fix rather than a silent
 * hole, which is exactly the shape of the bug adversarial review found in the first cut.
 */
export const NAV_BLOCK_ATTR = 'data-crm-nav-block';

/**
 * Surfaces whose MERE PRESENCE turns the nav keys off.
 *
 * ⚠ PRESENCE, NOT FOCUS — and that distinction is the whole point of this arm. The first cut of this
 * guard blocked on focus, and adversarial review found a DESTRUCTIVE bug in it: several modals the
 * record panel spawns take no focus at all (`DismissReasonModal` and `LogPassModal` had neither
 * `role="dialog"` nor an autoFocus; the layout editor and the note overlay are `role="dialog"` but
 * never focus themselves). Focus therefore stayed on the trigger button *inside the panel* — which
 * `RECORD_PANEL_ATTR` deliberately makes navigable — so `→` swapped the record while the modal stayed
 * mounted, and Confirm then wrote against a record the operator never chose (a terminal Log Pass, in
 * the worst case). A mounted modal means the operator is mid-decision; where the caret happens to sit
 * is not evidence about that.
 *
 * `[role="dialog"]:not([data-crm-record-panel])` still excludes only the record panel, so every OTHER
 * dialog blocks: `CrmSidePanel` (the columns / filter / sort docks), the note overlay, the fact-edit
 * modal, the mail modal, the layout editor, and the dismiss / pass / merge modals.
 */
export const BLOCKING_SURFACE_SELECTOR =
  `.ag-cell-inline-editing, .ag-popup-editor, [${NAV_BLOCK_ATTR}],`
  + ` [role="dialog"]:not([${RECORD_PANEL_ATTR}])`;

/**
 * Is a blocking surface mounted anywhere? Document-scoped on purpose: AG portals its popup editors to
 * `document.body` (`popupParent`), and a modal rendered as a SIBLING of the panel is the common shape
 * here, so a subtree-scoped query would miss both.
 */
export function hasBlockingSurface(root: ParentNode = document): boolean {
  return !!root.querySelector(BLOCKING_SURFACE_SELECTOR);
}

/**
 * Is focus somewhere that owns the keystroke itself? Text entry is the one arm that must stay
 * FOCUS-scoped rather than presence-scoped: the record panel is full of inputs, so blocking on their
 * mere presence would disable the feature outright. Typing must never swap the record out from under
 * the writer — the original reason this guard exists at all.
 */
export function isTypingTarget(active: Element | null | undefined): boolean {
  if (!active) return false;
  const el = active as HTMLElement;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT'
    || el.isContentEditable;
}

/** The full guard: a blocking surface is mounted, or focus is in a text-entry target. */
export function navKeysBlocked(
  active: Element | null | undefined = typeof document === 'undefined' ? null : document.activeElement,
  root: ParentNode = document,
): boolean {
  return isTypingTarget(active) || hasBlockingSurface(root);
}

/** Just the prev/next pair this hook needs off `recordNav` — the rest of that memo is irrelevant here. */
export interface RecordKeyNavTargets {
  prevId: number | null;
  nextId: number | null;
}

/**
 * Arm `←`/`→` while a record is open.
 *
 * Owner rulings 2026-08-20, all final:
 * - `→` next record, `←` previous record, **unconditional** while the panel is open ("when the panel
 *   is open, there's no need for H scroll"). `preventDefault` so the focused panel does not scroll
 *   sideways under the keystroke.
 * - `↑`/`↓` are **panel scroll** and are not handled here at all — the dead `ArrowUp`/`ArrowDown`
 *   record-nav branches were removed rather than repaired.
 * - `j`/`k` are **gone** ("kill j k"). They never worked and nothing depends on them.
 *
 * The scope limit is what makes the unconditional ruling safe: with no record open the effect is not
 * installed, so AG keeps its normal cell navigation for anyone browsing the grid without the panel.
 *
 * `e.repeat` keystrokes are dropped: holding the key down would otherwise walk the list at the OS
 * key-repeat rate, each step costing two record fetches plus a redraw and a scroll.
 */
export function useRecordKeyNav(
  recEntryId: number | null,
  targets: RecordKeyNavTargets | null,
  openRecord: (entryId: number) => void,
): void {
  useEffect(() => {
    if (recEntryId == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (navKeysBlocked()) return;
      if (!targets) return;
      if (e.key === 'ArrowRight' && targets.nextId != null) {
        e.preventDefault();
        openRecord(targets.nextId);
      } else if (e.key === 'ArrowLeft' && targets.prevId != null) {
        e.preventDefault();
        openRecord(targets.prevId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [recEntryId, targets, openRecord]);
}
